import type { ChangeEvent, ReactElement } from 'react';
import React, { useCallback, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { WMButtonVariant, WMSwitch } from '@walkme/wm-ui';
import { getCookieByKey, removeCookieByKey, setCookieByKey } from './cookie';
import StyledWMDialog from '../StyledWMDialog/StyledWMDialog';
import classes from './FeatureFlagDialog.module.scss';
import iconFF from '../../../../assets/icons/check-icon.svg';

export type FeatureFlags = { [key: string]: boolean };
export type StorageType = 'cookie' | 'localStorage';

export const FEATURE_FLAGS_KEY = 'featureFlags';

export const getItem = (storage: StorageType, key: string) => {
    try {
        switch (storage) {
            case 'localStorage':
                // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                return JSON.parse(localStorage.getItem(key));
            case 'cookie': {
                return getCookieByKey(key);
            }
        }
    } catch (e) {
        return null;
    }
};

const setItem = (storage: StorageType, key: $TSFixMe, value: string) => {
    try {
        switch (storage) {
            case 'localStorage':
                localStorage.setItem(key, value);
                break;
            case 'cookie': {
                setCookieByKey(key, value);
                break;
            }
        }
    } catch (e) {}
};

const removeItem = (storage: StorageType, key: $TSFixMe) => {
    try {
        switch (storage) {
            case 'localStorage':
                localStorage.removeItem(key);
                break;
            case 'cookie': {
                removeCookieByKey(key);
                break;
            }
        }
    } catch (e) {}
};

function FeatureFlagDialog({
    keys = [],
    accountFeatures,
    defaultFeatures = undefined,
    onChange,
    hostKey = 'shift+ctrl+up',
    'data-testid': dataTestid = 'FeatureFlagDialog',
    storage = 'localStorage'
}: {
    keys: string[];
    accountFeatures: $TSFixMe;
    defaultFeatures: $TSFixMe;
    // @ts-expect-error ts-migrate(7051) FIXME: Parameter has a name but no type. Did you mean 'ar... Remove this comment to see the full error message
    onChange?: (FeatureFlags) => void;
    hostKey?: string;
    'data-testid'?: string;
    storage?: StorageType;
}): ReactElement {
    const [localFeatureFlags, setLocalFeatureFlags] = useState(defaultFeatures);
    const [openDialog, setOpenDialog] = useState(false);

    useHotkeys(hostKey, () => setOpenDialog(true));

    const resetDialog = useCallback(() => {
        setLocalFeatureFlags(accountFeatures);
    }, [accountFeatures]);

    const saveDialog = useCallback(() => {
        const diffs = {};

        Object.entries(localFeatureFlags).forEach(([key, value]) => {
            if (accountFeatures[key] !== value) {
                diffs[key] = value;
            }
        });

        if (Object.keys(diffs).length > 0) {
            setItem(storage, FEATURE_FLAGS_KEY, JSON.stringify(diffs));
        } else {
            removeItem(storage, FEATURE_FLAGS_KEY);
        }

        if (onChange) {
            onChange(diffs);
        }

        setOpenDialog(false);
    }, [storage, onChange, localFeatureFlags, accountFeatures]);

    const onEnter = useCallback(() => {
        setLocalFeatureFlags(defaultFeatures);
    }, [defaultFeatures]);

    const onClose = () => {
        setOpenDialog(false);
    };

    const getLabel = (key: $TSFixMe) => {
        return (
            <span className={classes.label}>
                {!!localFeatureFlags?.[key] === !!accountFeatures?.[key] && <img alt={iconFF} src={iconFF} />}

                {localFeatureFlags[key] ? `${key} is ON 🤩` : `${key} is OFF 😴`}
            </span>
        );
    };

    return (
        <StyledWMDialog
            data-testid={dataTestid}
            title="Hidden Options"
            open={openDialog}
            TransitionProps={{ onEnter }}
            onClose={onClose}
            className={classes.dialogRoot}
            buttons={[
                {
                    className: classes.resetButton,
                    children: <>RESET</>,
                    'data-testid': `${dataTestid}-resetButton`,
                    onClick: resetDialog,
                    variant: WMButtonVariant.Secondary
                } as $TSFixMe,
                {
                    className: classes.okButton,
                    children: 'OK',
                    'data-testid': `${dataTestid}-okButton`,
                    onClick: saveDialog,
                    variant: WMButtonVariant.Primary
                } as $TSFixMe
            ]}
        >
            <p>
                {keys.map(key => (
                    <WMSwitch
                        key={key}
                        inputProps={{ 'data-testid': `${dataTestid}-${key}-switch` } as $TSFixMe}
                        checked={!!localFeatureFlags[key]}
                        label={getLabel(key)}
                        onChange={(event: ChangeEvent<HTMLInputElement>, newChecked: boolean) => {
                            setLocalFeatureFlags((all: $TSFixMe) => ({
                                ...all,
                                [key]: newChecked
                            }));
                        }}
                    />
                ))}
            </p>
        </StyledWMDialog>
    );
}

export default FeatureFlagDialog;
